import yaml from 'js-yaml';
export class LegalMarkdownProcessor {
    config = {};
    variables = {};
    sectionReferences = new Map();
    sectionCounters = [0, 0, 0, 0, 0, 0];
    definedTerms = new Map();
    /**
     * Process a legal markdown template with YAML front matter
     */
    processTemplate(template, customVariables) {
        // Extract YAML front matter
        const { frontMatter, content } = this.extractFrontMatter(template);
        // Parse YAML config
        this.config = frontMatter;
        this.variables = { ...frontMatter.variables, ...customVariables };
        console.log('[LegalMarkdownProcessor] Processing template:', {
            frontMatterVars: Object.keys(frontMatter.variables || {}).length,
            customVars: Object.keys(customVariables || {}).length,
            totalVars: Object.keys(this.variables).length,
            placeholderVars: Object.keys(this.variables).filter(k => k.startsWith('placeholder_')).length,
            sampleVars: Object.keys(this.variables).slice(0, 10).map(k => ({ [k]: this.variables[k] }))
        });
        // Process the content
        let processed = content;
        // Step 1: Process @today and date functions
        processed = this.processDateFunctions(processed);
        // Step 2: Process special blocks (red herring, signatures, etc.)
        processed = this.processSpecialBlocks(processed);
        // Step 3: Collect section references
        processed = this.collectSectionReferences(processed);
        // Step 4: Process conditional clauses
        processed = this.processConditionals(processed);
        // Step 5: Process variable substitution
        processed = this.processVariables(processed);
        // Step 6: Process placeholder substitution
        processed = this.processPlaceholders(processed);
        // Step 7: Process auto-numbering with styling
        processed = this.processAutoNumberingEnhanced(processed);
        // Step 8: Process cross-references
        processed = this.processCrossReferences(processed);
        // Step 9: Process defined terms
        processed = this.processDefinedTerms(processed);
        console.log('[LegalMarkdownProcessor] Final processed output:', {
            length: processed.length,
            firstLines: processed.split('\n').slice(0, 20).join('\n'),
            hasYamlFrontMatter: processed.startsWith('---'),
            hasMarkdownHeadings: processed.includes('#')
        });
        return processed;
    }
    /**
     * Extract YAML front matter from template
     */
    extractFrontMatter(template) {
        const frontMatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
        const match = template.match(frontMatterRegex);
        if (!match) {
            return { frontMatter: {}, content: template };
        }
        try {
            const frontMatter = yaml.load(match[1]);
            return { frontMatter, content: match[2] };
        }
        catch (error) {
            console.error('Error parsing YAML front matter:', error);
            return { frontMatter: {}, content: template };
        }
    }
    /**
     * Process date functions like @today
     */
    processDateFunctions(content) {
        const today = new Date();
        const formattedDate = today.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        return content.replace(/@today/g, formattedDate);
    }
    /**
     * NEW: Process special block markers (!!!)
     */
    processSpecialBlocks(content) {
        // Process red herring blocks
        content = this.processRedHerringBlocks(content);
        // Process signature blocks
        content = this.processSignatureBlocks(content);
        // Process wire instruction blocks
        content = this.processWireInstructionBlocks(content);
        return content;
    }
    /**
     * Process red herring warning blocks
     */
    processRedHerringBlocks(content) {
        const redHerringRegex = /!!!red-herring\s*\n([\s\S]*?)\n!!!/g;
        return content.replace(redHerringRegex, (match, warningText) => {
            const styling = this.config.styling?.['red-herring'] || {};
            const style = styling.style || 'border-box';
            const emphasis = styling.emphasis || 'all-caps-bold';
            const text = warningText.trim();
            const emphasizedText = emphasis === 'all-caps-bold'
                ? `**${text.toUpperCase()}**`
                : emphasis === 'bold'
                    ? `**${text}**`
                    : text;
            if (style === 'border-box') {
                return `\n> 🚨 **[IMPORTANT DISCLOSURE]**\n>\n> ${emphasizedText}\n`;
            }
            else if (style === 'blockquote') {
                return `\n> ${emphasizedText}\n`;
            }
            else {
                return `\n${emphasizedText}\n`;
            }
        });
    }
    /**
     * Process signature block markers
     */
    processSignatureBlocks(content) {
        const signatureRegex = /!!!signature-block\s*\n([\s\S]*?)\n!!!/g;
        return content.replace(signatureRegex, (match, signatureContent) => {
            // Add horizontal rule for separation
            return `\n---\n\n${signatureContent.trim()}\n\n---\n`;
        });
    }
    /**
     * Process wire instruction blocks
     */
    processWireInstructionBlocks(content) {
        const wireRegex = /!!!wire-instructions\s*\n([\s\S]*?)\n!!!/g;
        return content.replace(wireRegex, (match, wireContent) => {
            return `\n> 💰 **WIRE TRANSFER INSTRUCTIONS**\n>\n${wireContent.trim().split('\n').map(line => '> ' + line).join('\n')}\n`;
        });
    }
    /**
     * Process conditional clauses [{{condition}}text]
     */
    processConditionals(content) {
        const conditionalRegex = /\[\{\{([^}]+)\}\}([^\]]*)\]/g;
        return content.replace(conditionalRegex, (match, conditionName, clauseText) => {
            const condition = this.variables[conditionName.trim()];
            if (condition === true || condition === 'true' || condition === 1 || condition === '1') {
                return clauseText;
            }
            return '';
        });
    }
    /**
     * Process variable substitution {{variable}}
     */
    processVariables(content) {
        const variableRegex = /\{\{([^}]+)\}\}/g;
        return content.replace(variableRegex, (match, variableName) => {
            const name = variableName.trim();
            const value = this.variables[name];
            if (value === undefined || value === null) {
                console.warn(`Variable "${name}" not found`);
                return match;
            }
            if (value instanceof Date) {
                return value.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }
            return String(value);
        });
    }
    /**
     * Process placeholder substitution
     */
    processPlaceholders(content) {
        const placeholderRegex = /\[([^\]]+?)\]/g;
        let replacementCount = 0;
        let skippedCount = 0;
        let notFoundCount = 0;
        const result = content.replace(placeholderRegex, (match, placeholderText) => {
            const text = placeholderText.trim();
            if (text.includes('{{') || text.includes('|')) {
                skippedCount++;
                return match;
            }
            const varName = `placeholder_${text.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}`;
            const value = this.variables[varName];
            if (value === undefined || value === null || value === '') {
                console.warn(`[LegalMarkdownProcessor] Placeholder "${text}" (${varName}) not found or empty`, {
                    availableKeys: Object.keys(this.variables).filter(k => k.includes(varName.split('_').slice(1, 3).join('_'))).slice(0, 3)
                });
                notFoundCount++;
                return match;
            }
            console.log(`[LegalMarkdownProcessor] Replaced placeholder "${text}" (${varName}) with:`, value);
            replacementCount++;
            return String(value);
        });
        console.log('[LegalMarkdownProcessor] Placeholder processing complete:', {
            replaced: replacementCount,
            skipped: skippedCount,
            notFound: notFoundCount,
            availablePlaceholders: Object.keys(this.variables).filter(k => k.startsWith('placeholder_'))
        });
        return result;
    }
    /**
     * Collect section references for cross-referencing
     */
    collectSectionReferences(content) {
        this.sectionReferences.clear();
        this.sectionCounters = [0, 0, 0, 0, 0, 0];
        const lines = content.split('\n');
        for (const line of lines) {
            // Check for Legal Markdown sections with reference markers: l. Title |refId|
            const sectionMatch = line.match(/^(l{1,6})\.\s+(.+?)\s*\|([^|]+)\|\s*$/);
            if (sectionMatch) {
                const level = sectionMatch[1].length - 1;
                const title = sectionMatch[2].trim();
                const refId = sectionMatch[3].trim();
                this.sectionCounters[level]++;
                for (let j = level + 1; j < 6; j++) {
                    this.sectionCounters[j] = 0;
                }
                const sectionNumber = this.generateSectionNumber(level);
                this.sectionReferences.set(refId, {
                    id: refId,
                    number: sectionNumber,
                    title: title
                });
            }
            // ALSO check for markdown headings with reference markers: # Title |refId|
            const headingMatch = line.match(/^(#{1,6})\s+(.+?)\s*\|([^|]+)\|\s*$/);
            if (headingMatch) {
                const level = headingMatch[1].length - 1; // # is level 0, ## is level 1, etc.
                const title = headingMatch[2].trim();
                const refId = headingMatch[3].trim();
                // For markdown headings, we don't use auto-numbering counters
                // Just use the heading level as the "section number"
                const sectionNumber = `Section ${level + 1}`;
                this.sectionReferences.set(refId, {
                    id: refId,
                    number: sectionNumber,
                    title: title
                });
            }
        }
        console.log('[LegalMarkdownProcessor] Collected section references:', {
            totalRefs: this.sectionReferences.size,
            references: Array.from(this.sectionReferences.entries()).map(([id, ref]) => ({
                id,
                number: ref.number,
                title: ref.title.substring(0, 50)
            }))
        });
        return content;
    }
    /**
     * Generate section number based on level and config
     */
    generateSectionNumber(level) {
        const formats = [
            this.config['level-1'] || '1.',
            this.config['level-2'] || 'a.',
            this.config['level-3'] || 'i.',
            this.config['level-4'] || '1.',
            this.config['level-5'] || 'a.',
            this.config['level-6'] || 'i.'
        ];
        const format = formats[level];
        const counter = this.sectionCounters[level];
        if (format.includes('1')) {
            return counter.toString();
        }
        else if (format.includes('a')) {
            return this.toLetters(counter);
        }
        else if (format.includes('A')) {
            return this.toLetters(counter).toUpperCase();
        }
        else if (format.includes('i')) {
            return this.toRoman(counter).toLowerCase();
        }
        else if (format.includes('I')) {
            return this.toRoman(counter);
        }
        return counter.toString();
    }
    /**
     * Detect rendering context with enhanced heuristics
     */
    detectContext(line, previousLines) {
        const recentContext = previousLines.slice(-10).join('\n').toLowerCase();
        const lineLower = line.toLowerCase();
        // High emphasis keywords (contract obligations)
        const highEmphasisKeywords = [
            'represents and warrants', 'covenants', 'agrees that',
            'acknowledges and agrees', 'investor represents',
            'subscriber represents', 'obligations created',
            'by receipt', 'execution', 'binding'
        ];
        // Medium emphasis keywords (legal provisions)
        const mediumEmphasisKeywords = [
            'acknowledges', 'agrees', 'warrants',
            'confidentiality', 'obligations',
            'investor acknowledgment', 'recipient',
            'the investor', 'the subscriber'
        ];
        // Keywords indicating major sections (use headings)
        const headingKeywords = [
            'risk factors', 'definitions', 'use of proceeds',
            'management team', 'financial projections',
            'offering terms', 'investment summary',
            'table of contents', 'exhibits'
        ];
        // Check for heading indicators
        const shouldUseHeading = headingKeywords.some(keyword => lineLower.includes(keyword) || recentContext.includes(keyword));
        if (shouldUseHeading) {
            return { renderAs: 'heading', emphasis: 'medium' };
        }
        // Check emphasis level for numbered paragraphs
        const isHighEmphasis = highEmphasisKeywords.some(keyword => lineLower.includes(keyword) || recentContext.includes(keyword));
        if (isHighEmphasis) {
            return { renderAs: 'numbered-paragraph', emphasis: 'high' };
        }
        const isMediumEmphasis = mediumEmphasisKeywords.some(keyword => lineLower.includes(keyword) || recentContext.includes(keyword));
        if (isMediumEmphasis) {
            return { renderAs: 'numbered-paragraph', emphasis: 'medium' };
        }
        // Default: numbered paragraph with low emphasis
        return { renderAs: 'numbered-paragraph', emphasis: 'low' };
    }
    /**
     * ENHANCED: Process auto-numbering with full styling support
     */
    processAutoNumberingEnhanced(content) {
        this.sectionCounters = [0, 0, 0, 0, 0, 0];
        const lines = content.split('\n');
        const processedLines = [];
        const previousLines = [];
        for (const line of lines) {
            const sectionMatch = line.match(/^(l{1,6})\.\s+(.+?)\s*(?:\|[^|]+\|)?$/);
            if (sectionMatch) {
                const level = sectionMatch[1].length - 1;
                const title = sectionMatch[2];
                // Increment counter
                this.sectionCounters[level]++;
                // Reset deeper levels
                for (let j = level + 1; j < 6; j++) {
                    this.sectionCounters[j] = 0;
                }
                // Generate section number
                const sectionNumber = this.generateSectionNumber(level);
                const fullNumber = this.buildFullSectionNumber(level);
                // Detect context and emphasis
                const context = this.detectContext(line, previousLines);
                if (context.renderAs === 'numbered-paragraph') {
                    // Render as numbered paragraph with appropriate emphasis
                    if (context.emphasis === 'high') {
                        // High emphasis: Bold number AND bold title
                        processedLines.push(`**${fullNumber}** **${title}**`);
                    }
                    else if (context.emphasis === 'medium') {
                        // Medium emphasis: Bold number, regular title
                        processedLines.push(`**${fullNumber}** ${title}`);
                    }
                    else {
                        // Low emphasis: Regular number, regular title
                        processedLines.push(`${fullNumber} ${title}`);
                    }
                }
                else {
                    // Render as heading
                    const headingLevel = Math.min(level + 3, 6);
                    const heading = '#'.repeat(headingLevel);
                    processedLines.push(`${heading} ${fullNumber} ${title}`);
                }
                previousLines.push(line);
            }
            else {
                processedLines.push(line);
                previousLines.push(line);
            }
        }
        return processedLines.join('\n');
    }
    /**
     * Build full section number
     */
    buildFullSectionNumber(currentLevel) {
        const numbers = [];
        for (let i = 0; i <= currentLevel; i++) {
            if (this.sectionCounters[i] > 0) {
                numbers.push(this.generateSectionNumber(i));
            }
        }
        return numbers.join('.');
    }
    /**
     * Process cross-references with improved fallback handling
     */
    processCrossReferences(content) {
        const lines = content.split('\n');
        const processedLines = [];
        for (const line of lines) {
            // For markdown headings, remove the reference markers |refId|
            if (line.match(/^#{1,6}\s/)) {
                const cleanedHeading = line.replace(/\s*\|([^|]+)\|\s*$/, '');
                processedLines.push(cleanedHeading);
                continue;
            }
            // Process cross-references in regular text
            const processedLine = line.replace(/\|([^|]+)\|/g, (match, refId) => {
                const trimmedRefId = refId.trim();
                const ref = this.sectionReferences.get(trimmedRefId);
                if (ref) {
                    // Found reference: create proper markdown link
                    return `[${ref.title}](#${trimmedRefId})`;
                }
                // If not found, convert to a readable format instead of leaving pipes
                console.warn(`[LegalMarkdownProcessor] Reference "${trimmedRefId}" not found. Available refs:`, Array.from(this.sectionReferences.keys()).slice(0, 5));
                // Convert underscores to spaces and title case for readability
                const readableName = trimmedRefId
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                return `**${readableName}**`;
            });
            processedLines.push(processedLine);
        }
        return processedLines.join('\n');
    }
    /**
     * NEW: Process defined terms with proper formatting
     */
    processDefinedTerms(content) {
        // Find defined terms (quoted and bolded, followed by "means")
        const definitionRegex = /"?\*\*([A-Z][a-zA-Z\s]+)\*\*"?\s+means/g;
        let match;
        while ((match = definitionRegex.exec(content)) !== null) {
            this.definedTerms.set(match[1], true);
        }
        // Optionally: Bold subsequent uses of defined terms
        // (This is configurable based on styling preferences)
        const styling = this.config.styling?.['defined-terms'];
        if (styling?.subsequent === 'bold') {
            this.definedTerms.forEach((_, term) => {
                // This would require more sophisticated text processing
                // to avoid bolding within already bolded text
                // Placeholder for future enhancement
            });
        }
        return content;
    }
    /**
     * Convert number to letters
     */
    toLetters(num) {
        let result = '';
        let n = num;
        while (n > 0) {
            const remainder = (n - 1) % 26;
            result = String.fromCharCode(97 + remainder) + result;
            n = Math.floor((n - 1) / 26);
        }
        return result;
    }
    /**
     * Convert number to Roman numerals
     */
    toRoman(num) {
        const romanNumerals = [
            [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
            [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
            [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']
        ];
        let result = '';
        let remaining = num;
        for (const [value, numeral] of romanNumerals) {
            while (remaining >= value) {
                result += numeral;
                remaining -= value;
            }
        }
        return result;
    }
}
/**
 * Helper function to process legal markdown template
 */
export function processLegalMarkdown(template, variables) {
    const processor = new LegalMarkdownProcessor();
    return processor.processTemplate(template, variables);
}
